/**
 * 場（フィールド）コンポーネント
 *
 * プレイヤーの場に出された駒を表示
 * - 攻め駒エリア
 * - 受け駒・伏せ駒エリア（同じ行に時系列順で表示）
 */

import React from 'react';
import { Piece } from './Piece';
import type { FieldPiece } from '@/types/game';

/**
 * 場コンポーネントProps
 */
interface FieldProps {
  /** プレイヤーの場の駒リスト */
  pieces: FieldPiece[];
  /** プレイヤーポジション */
  playerPosition: number;
  /** プレイヤー名 */
  playerName: string;
  /** 現在のターンプレイヤーか */
  isCurrentTurn?: boolean;
  /** 現在の攻め駒 */
  currentAttackPiece?: string | null;
  /** 現在攻めているプレイヤーのポジション */
  currentAttackPlayerPosition?: number | null;
}

// 駒の基本サイズ
const PIECE_WIDTH = 60;
const PIECE_HEIGHT = 80;

/**
 * 場コンポーネント
 *
 * プレイヤーの場に出された駒を種類別に表示
 */
export const Field: React.FC<FieldProps> = ({
  pieces,
  playerPosition,
  playerName,
  isCurrentTurn = false,
  currentAttackPiece,
  currentAttackPlayerPosition,
}) => {
  // 駒をアクション種類別に分類
  const attackPieces = pieces.filter((fp) => fp.action === 'ATTACK');
  // 受け駒と伏せ駒は同じ行に時系列順で表示（何巡目に伏せたか把握可能）
  const receiveAndHiddenPieces = pieces.filter(
    (fp) => fp.action === 'RECEIVE' || fp.action === 'HIDDEN_PLACE'
  );

  // プレイヤーポジションに応じたflex方向と表示順序
  // P0,P2（下・上）: flex-col（縦方向）
  // P1,P3（右・左）: flex-row（横方向）
  const isVertical = playerPosition === 0 || playerPosition === 2;
  const flexDirection = isVertical ? 'flex flex-col' : 'flex flex-row';

  // 駒配置の方向（セクション内）
  // P0（下）: 横並び・左から右
  // P1（右）: 縦並び・下から上（reverse）
  // P2（上）: 横並び・右から左（reverse）
  // P3（左）: 縦並び・上から下
  const piecesFlexClass =
    playerPosition === 0 ? 'flex flex-wrap' :           // P0: 横並び・左から右
    playerPosition === 1 ? 'flex flex-col-reverse' :    // P1: 縦並び・下から上
    playerPosition === 2 ? 'flex flex-row-reverse flex-wrap' :  // P2: 横並び・右から左
    'flex flex-col';                                    // P3: 縦並び・上から下

  // 駒の向き（縦長 or 横長）
  // P0, P2（下・上）: 縦長
  // P1, P3（右・左）: 横長
  const pieceOrientation = isVertical ? 'vertical' : 'horizontal';

  // 表示順序
  // P0（下）、P1（右）: 受け駒・伏せ駒が先（1番目）、攻め駒が後（2番目）
  // P2（上）、P3（左）: 攻め駒が先（1番目）、受け駒・伏せ駒が後（2番目）
  const receiveFirst = playerPosition === 0 || playerPosition === 1;

  // 駒の空プレースホルダーサイズ（向きに応じて変更）
  const emptySlotWidth = pieceOrientation === 'horizontal' ? PIECE_HEIGHT : PIECE_WIDTH;  // 横長: 80px, 縦長: 60px
  const emptySlotHeight = pieceOrientation === 'horizontal' ? PIECE_WIDTH : PIECE_HEIGHT;  // 横長: 60px, 縦長: 80px
  const emptySlotStyle = {
    width: `${emptySlotWidth}px`,
    height: `${emptySlotHeight}px`,
    borderRadius: '6px',
  };

  // 攻め駒の最大スロット数（ごいたの手駒8枚から4枠）
  const MAX_ATTACK_SLOTS = 4;
  // 受け駒・伏せ駒の最大スロット数（4枠）
  const MAX_RECEIVE_SLOTS = 4;

  // 攻め駒セクション（常に表示・各駒位置に黒枠プレースホルダー）
  const attackSection = (
    <div>
      <div className="text-xs text-gray-600">攻め駒</div>
      <div className={`${piecesFlexClass} gap-2`}>
        {Array.from({ length: MAX_ATTACK_SLOTS }).map((_, index) => {
          const fieldPiece = attackPieces[index];

          if (fieldPiece) {
            // 最新の攻め駒（配列の最後の要素）かつ、現在の攻め駒と一致し、
            // かつ自分が現在攻めているプレイヤーの場合のみ赤枠を表示
            const isLastAttackPiece = index === attackPieces.length - 1;
            const isCurrentAttacker = playerPosition === currentAttackPlayerPosition;
            const isCurrentAttackPiece =
              isLastAttackPiece &&
              isCurrentAttacker &&
              currentAttackPiece === fieldPiece.piece.type;

            return (
              <Piece
                key={`attack-${index}`}
                piece={fieldPiece.piece}
                disabled={true}
                orientation={pieceOrientation}
                isCurrentAttack={isCurrentAttackPiece}
              />
            );
          } else {
            // 空のプレースホルダー（黒枠）
            return (
              <div
                key={`attack-empty-${index}`}
                className="border-2 border-gray-800 bg-gray-100"
                style={emptySlotStyle}
              />
            );
          }
        })}
      </div>
    </div>
  );

  // 受け駒・伏せ駒セクション（常に表示・各駒位置に黒枠プレースホルダー）
  const receiveSection = (
    <div>
      <div className="text-xs text-gray-600">受け駒・伏せ駒</div>
      <div className={`${piecesFlexClass} gap-2`}>
        {Array.from({ length: MAX_RECEIVE_SLOTS }).map((_, index) => {
          const fieldPiece = receiveAndHiddenPieces[index];

          if (fieldPiece) {
            return (
              <Piece
                key={`receive-hidden-${index}`}
                piece={fieldPiece.piece}
                disabled={true}
                orientation={pieceOrientation}
              />
            );
          } else {
            // 空のプレースホルダー（黒枠）
            return (
              <div
                key={`receive-empty-${index}`}
                className="border-2 border-gray-800 bg-gray-100"
                style={emptySlotStyle}
              />
            );
          }
        })}
      </div>
    </div>
  );

  // Field全体の最大サイズ
  const maxSizeStyle = isVertical
    ? { maxWidth: '600px' }
    : { maxHeight: '320px' };

  return (
    <div
      className={`
        p-2 rounded-lg border-2
        ${isCurrentTurn ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300 bg-white'}
        transition-all duration-300
      `}
      style={maxSizeStyle}
    >
      {/* プレイヤー名表示 */}
      <div>
        <h3 className="text-sm font-bold text-black">
          {playerName} (P{playerPosition})
        </h3>
      </div>

      {/* 駒表示エリア（プレイヤーポジションに応じた方向と順序） */}
      <div className={flexDirection}>
        {receiveFirst ? (
          <>
            {receiveSection}
            {attackSection}
          </>
        ) : (
          <>
            {attackSection}
            {receiveSection}
          </>
        )}
      </div>
    </div>
  );
};
